import { PdpAudit } from "../../../components/PdpAudit/types";
import { ConsumerVoiceAudit } from "../../../types/consumerVoice";

export interface PrepPackBrand {
    id: number;
    name: string;
    logo?: string | null;
}

export interface ProductCategory {
    id: number;
    name: string;
    slug?: string;
    parent_id?: number | null;
}

export interface PrepPackProduct {
    desireid: number;
    title: string;
    url: string | null;
    product_categories?: ProductCategory[];
}

export interface NamedItem {
    title: string;
    body: string;
}

export interface KeyValueRow {
    label: string;
    value: string;
}

export interface ObjectionResponse {
    objection: string;
    response: string;
}

// Legacy (v1) single-call report shape. Packs generated before the
// engine-parity pipeline keep rendering through this; new packs carry a
// PrepPackReportV2 envelope (distinguished by `version: 2`).
export interface PrepPackReport {
    title?: string;
    subtitle?: string;
    executiveSummary?: {
        overview?: string;
        cheatSheet?: {
            leadLine?: string;
            threePointsToLeadWith?: NamedItem[];
            topObjectionsAndAnswers?: ObjectionResponse[];
            recommendation?: string;
            ask?: string;
        };
        howToReadThisPack?: string;
    };
    companyAndBrandProfile?: {
        intro?: string;
        atAGlance?: KeyValueRow[];
        flagshipProducts?: NamedItem[];
        secondaryProductLines?: NamedItem[];
    };
    decisionMakerIntelligence?: {
        orgStructureNote?: string;
        primaryContact?: {
            name?: string;
            title?: string;
            background?: string;
            priorities?: string[];
            howToEngage?: string;
        };
        supportingContacts?: Array<{ name?: string; title?: string; notes?: string }>;
    };
    categoryLandscape?: {
        marketSizeAndGrowth?: string;
        competitiveSet?: NamedItem[];
        whatConverts?: string[];
        regulatoryEnvironment?: string;
    };
    voiceOfCustomer?: {
        coverageSummary?: string;
        categoryFindings?: string[];
        painPoints?: NamedItem[];
        topThemes?: NamedItem[];
        consumerObjections?: NamedItem[];
        activationMap?: NamedItem[];
        customerLanguageCopyBank?: string[];
    };
    meetingGamePlan?: {
        discoveryQuestions?: string[];
        objectionsAndResponses?: ObjectionResponse[];
        proofPoints?: string[];
    };
    tdcPackageRecommendation?: {
        recommendedPackage?: string;
        rationale?: string;
        assetList?: string[];
        activationMapByFunnelStage?: NamedItem[];
        pilotScopeAndSuccessMetrics?: string;
        recommendedExperts?: NamedItem[];
    };
    sourceIndex?: {
        companySources?: string[];
        voiceOfCustomerSources?: string[];
    };
}

// ---------------------------------------------------------------------------
// v2 (engine-parity) report types — mirrors desirelist-api
// src/lib/prepPack/schema/prepPackDoc.js. The doc is a typed block tree;
// citation numbers and the Source Index are assigned by backend code and
// arrive precomputed in the envelope.
// ---------------------------------------------------------------------------

export type DocSourceType =
    | "company"
    | "category"
    | "regulatory"
    | "reddit"
    | "internal"
    | "other";

export interface DocSource {
    publication: string;
    title: string;
    url: string;
    type: DocSourceType;
    accessed_at?: string;
    snippet?: string;
}

export interface ProseBlock {
    type: "prose";
    text: string;
    cites: string[];
}
export interface BulletsBlock {
    type: "bullets";
    items: string[];
    cites: string[];
}
export interface InferenceBlock {
    type: "inference";
    text: string;
}
export interface CalloutBlock {
    type: "callout";
    label: string;
    text: string;
    cites: string[];
}
export interface CheatSheetBlock {
    type: "cheat_sheet";
    lead_line: string;
    three_points: Array<{ label: string; body: string; cites: string[] }>;
    top_objections: Array<{ objection: string; answer: string; cites: string[] }>;
    recommendation: string;
    ask: string;
}
export interface AtAGlanceBlock {
    type: "at_a_glance";
    rows: Array<{ attribute: string; detail: string; cites: string[] }>;
}
export interface CompetitiveSetBlock {
    type: "competitive_set";
    rows: Array<{ brand: string; position: string; notable: string; cites: string[] }>;
}
export interface PainPointBlock {
    type: "pain_point_library";
    rows: Array<{
        theme: string;
        pain_point: string;
        persona: string;
        scope: string;
        freq: "High" | "Medium" | "Low";
        cites: string[];
    }>;
}
export interface TopThemesBlock {
    type: "top_themes";
    rows: Array<{ theme: string; insight: string; angle: string; cites: string[] }>;
}
export interface ConsumerObjectionsBlock {
    type: "consumer_objections";
    rows: Array<{ objection: string; counter_message: string; cites: string[] }>;
}
export interface ActivationMapBlock {
    type: "activation_map";
    rows: Array<{
        theme: string;
        funnel_stage: string;
        content_type: string;
        channels: string;
    }>;
}
export interface CopyBankBlock {
    type: "copy_bank";
    rows: Array<{ phrase: string; cites: string[] }>;
}
export interface ObjectionsResponsesBlock {
    type: "objections_responses";
    rows: Array<{ objection: string; response: string; cites: string[] }>;
}
export interface DiscoveryQuestionsBlock {
    type: "discovery_questions";
    questions: string[];
}
export interface ProofPointsBlock {
    type: "proof_points";
    points: Array<{ point: string; cites: string[] }>;
}
export interface AssetListBlock {
    type: "asset_list";
    assets: Array<{ name: string; description: string; formats: string[] }>;
}
export interface FunnelStageMapBlock {
    type: "funnel_stage_map";
    rows: Array<{
        funnel_stage: string;
        objective: string;
        asset_type: string;
        placement: string;
    }>;
}

export type DocBlock =
    | ProseBlock
    | BulletsBlock
    | InferenceBlock
    | CalloutBlock
    | CheatSheetBlock
    | AtAGlanceBlock
    | CompetitiveSetBlock
    | PainPointBlock
    | TopThemesBlock
    | ConsumerObjectionsBlock
    | ActivationMapBlock
    | CopyBankBlock
    | ObjectionsResponsesBlock
    | DiscoveryQuestionsBlock
    | ProofPointsBlock
    | AssetListBlock
    | FunnelStageMapBlock;

export interface DocDeeperSubsection {
    title: string;
    blocks: DocBlock[];
}

export interface DocSubsection {
    number: string;
    title: string;
    blocks: DocBlock[];
    deeper_subsections?: DocDeeperSubsection[];
}

export interface DocSection {
    id: string;
    title: string;
    intro_blocks: DocBlock[];
    subsections: DocSubsection[];
}

export interface DocParentBlock {
    input_company: string;
    parent_name: string;
    brand_in_scope: string;
    relationship: string;
    evidence_source_key: string;
    notes?: string;
    correction_detected: boolean;
}

export interface DocLabelClaim {
    claim_text: string;
    product: string;
    exact_quote: string;
    source_key: string;
    page_or_url_anchor?: string;
}

export interface PrepPackDoc {
    meta: {
        brand: string;
        parent_company: string;
        prepared_for?: string;
        prepared_by?: string;
        run_id: string;
        generated_at: string;
    };
    parent_block: DocParentBlock;
    sections: DocSection[];
    label_claims: DocLabelClaim[];
    sources: Record<string, DocSource>;
}

export interface SourceIndexEntry {
    n: number;
    key: string;
    publication: string;
    title: string;
    url: string;
}

export interface PrepPackExpert {
    userid: number;
    slug: string;
    name: string;
    profession: string | null;
    imageUrl: string | null;
    archetype: string;
    rank: number;
    whyFits: string;
    expertIq: {
        score: number | null;
        affinity: number | null;
        phrase: string | null;
    };
}

export type QaVerdict = "PASS" | "REVIEW" | "FAIL";

export interface PrepPackReportV2 {
    version: 2;
    doc: PrepPackDoc;
    // source_key -> assigned [N] (company range low, reddit range high)
    citations: Record<string, number>;
    sourceIndex: { company: SourceIndexEntry[]; reddit: SourceIndexEntry[] };
    qa: { verdict: QaVerdict; findings: string };
    parentCorrection: {
        detected: boolean;
        input: string;
        corrected: string;
        reason: string;
    };
    experts: PrepPackExpert[];
    warnings: string[];
    manifest: {
        promptsHash: string;
        provider: string;
        model: string;
        engineVersion: string;
    };
}

export type AnyPrepPackReport = PrepPackReport | PrepPackReportV2;

export const isV2Report = (
    report: AnyPrepPackReport | null | undefined,
): report is PrepPackReportV2 =>
    !!report && (report as PrepPackReportV2).version === 2;

// A pdp_audit_reports row generated for one of the pack's product_urls.
// Only present on the detail GET (list payloads stay light).
export interface PrepPackAudit {
    id: number;
    url: string;
    status: string;
    generation_error?: string | null;
    report: PdpAudit | null;
}

export interface PrepPack {
    id: number;
    brand_id: number | null;
    brand_name: string | null;
    // "Brand — Product focus" (falls back to the brand on older packs).
    title?: string | null;
    product_urls: string[];
    rmn_brand_id?: number | null;
    user_id: number | null;
    category: string;
    lead_source: string | null;
    meeting_type: string | null;
    meeting_goal: string | null;
    decision_maker: string | null;
    notes: string | null;
    inputs: Record<string, any>;
    report: AnyPrepPackReport | null;
    status: "generating" | "ready" | "error";
    // Pipeline stage while generating ('audits' | 'discovery' | 'research' |
    // 'fact_check_voc' | 'assemble' | 'expert_curation' | 'qa' | 'done').
    stage?: string | null;
    llm_status?: string | null;
    llm_error?: string | null;
    llm_provider?: string | null;
    // Legacy aliases still emitted by the API during the rename transition.
    gemini_status: string | null;
    gemini_error: string | null;
    created_at: string;
    updated_at: string;
    brand: PrepPackBrand | null;
    // The user who requested the pack (resolved from user_id; null on
    // legacy rows or when the API omitted the join).
    user?: { userid: number; email: string | null; firstName?: string | null; lastName?: string | null } | null;
    products: PrepPackProduct[];
    audits?: PrepPackAudit[];
    // One Consumer Voice analysis per product (detail GET only).
    cv_audits?: ConsumerVoiceAudit[];
    // AI Presence analyses for the same products (zip's ai-presence/ folder).
    aip_audits?: import('../../../types/aiPresence').AiPresenceAudit[];
}

export interface CreatePrepPackPayload {
    brand_name: string;
    product_urls: string[];
    category: string;
    lead_source?: string;
    meeting_type?: string;
    meeting_goal?: string;
    decision_maker?: string;
    notes?: string;
    inputs?: Record<string, any>;
}

// The product focus half of a pack title ("Brand — Product focus"); empty
// on older packs titled by the brand alone.
export const prepPackFocus = (p: Pick<PrepPack, "title">): string => {
    const t = p.title || "";
    const i = t.indexOf(" — ");
    return i >= 0 ? t.slice(i + 3) : "";
};
